(ns dre.pages.search.events
  (:require
   [clojure.string :as str]
   [day8.re-frame.tracing :refer-macros [fn-traced]]
   [dre.app.router :as router :refer [->route]]
   [dre.app.search.query-shared :as qs]
   [dre.app.events :as app-events]
   [dre.components.search-results.events :as-alias results]
   [dre.components.search-results.db :as results-db]
   [dre.components.searchbar.events :as-alias searchbar-events]
   [dre.network :as-alias network]
   [dre.page-util :refer [query-params]]
   [dre.shared.interface.search.pills :as pills]
   [dre.shared.interface.search.blunt :as blunt]
   [dre.utils :refer [stringify-keys-with-namespaces roles-hash]]
   [dre.utils.query :as query-utils]
   [dre.widgets.events :as-alias widgets]
   [re-frame.core :as rf]
   [superstructor.re-frame.fetch-fx :as-alias fetch-fx]))

(def db-path :search)


(defn add-pills*
  "Returns merged pills"
  [pills other]
  (vec (concat [pills/lbracket] pills [pills/rbracket]
               [pills/lbracket] other [pills/rbracket])))

(defn filtered-query
  [old-query filters]
  (update old-query :query/pills add-pills* (:query/pills filters)))

(def combination-namespaces
  "Namespaces where filters should be grouped by category and:
  1) filters within a category combined with AND operator
  2) categories combined with OR operator"
  #{:demographics})

(defn filter-pills
  ([filters]
   (filter-pills filters []))
  ([filters exclude]
   (let [bool-combine (fn [bool-pill f]
                        (if (> (count f) 1)
                          (concat [pills/lbracket]
                                  (interpose bool-pill f)
                                  [pills/rbracket])
                          f))
         or-pill {:pill/type :syntax, :pill/query "or"}
         and-pill {:pill/type :syntax, :pill/query "and"}
         or-combine (partial bool-combine or-pill)
         and-combine (partial bool-combine and-pill)]
     {:query/type "articles"
      :query/pills
      (filter
       identity
       (mapcat
        (fn [[k f]]
          (if (contains? combination-namespaces k)
            (->> (sort-by :pill/category f)
                 (partition-by :pill/category)
                 (map or-combine)
                 and-combine
                 flatten)
            (or-combine f)))
        (apply dissoc filters exclude)))})))

(defn analysis-query [base-query widget-filters exclude]
  (let [filters (filter-pills widget-filters exclude)
        filtered-query (-> (filtered-query base-query filters)
                           (update :query/pills pills/coerce))]
    [filtered-query (query-utils/pills-api-params filtered-query)]))

(defn- promoted-query
  [base-query widget-filters]
  (let [filters (filter-pills widget-filters)
        promoted-query (-> base-query
                           (filtered-query filters)
                           (update :query/pills pills/coerce))]
    promoted-query))


(defn remove-from-query
  [search-query parent pill children]
  (let [children (->> children
                      (remove #(= (:pill/query %) (:pill/query pill)))
                      (map #(pills/term-pill* (:pill/query %) (:pill/label %))))
        children (vec (if (> (count children) 1)
                        (concat [pills/lbracket]
                                (interpose pills/or-op children)
                                [pills/rbracket])
                        children))
        rf (fn [acc p]
             (vec (if (and (= :senses (:pill/type p))
                           (= (:pill/query p) (:pill/query parent)))
                    (concat acc children)
                    (conj acc p))))
        uf #(reduce rf [] %)]
    (update search-query :query/pills uf)))

(defn replace-in-query
  [search-query parent pill]
  (let [from-sense (:pill/query parent)
        to-sense (:pill/query pill)
        to-label (:pill/label pill)
        mf (fn [m]
             (if (and (= :senses (:pill/type m))
                      (= from-sense (:pill/query m)))
               (pills/term-pill* to-sense to-label)
               m))
        uf #(mapv mf %)]
    (update search-query :query/pills uf)))

(defn route-redirect?
  "Returns true if app should redirect to saved route."
  [route new-route]
  (and (not (:query (query-params new-route)))
       (:query (query-params route))))

;; -- Events

(rf/reg-event-fx
 ::init
 [rf/trim-v]
 (fn-traced
  [{db :db} [new-route]]
  (let [reload? (:reload? (query-params new-route))
        new-route (dissoc new-route :reload?)
        old-route (get-in db [db-path :route])
        redirect? (route-redirect? old-route new-route)]
    (if redirect?
      {:db db
       :fx [[:dispatch [::router/redirect-to-path-with-query-params (->route :search) (query-params old-route)]]]}
      (let [not-same-query? (not= (:query (query-params new-route)) (:query (query-params old-route)))
            search-query (qs/search-query new-route)
            not-same-order? (not= (:order (query-params new-route)) (:order (query-params old-route)))]
        {:db (cond-> db
               true (assoc-in [db-path :route] new-route)
               not-same-query? (assoc-in [db-path :search-query] {:query/type :articles
                                                                  :query/pills (blunt/parse (:query/query search-query))})
               not-same-query? (assoc-in [:boost-query] nil)
               not-same-query? (assoc-in [db-path :widget-filters] {})
               not-same-query? (assoc-in [db-path :last-selected-filter-key] nil))
         :fx [(when not-same-query? [:dispatch [::widgets/init (:query/viewtype search-query)]])
              (when (or not-same-order? not-same-query?) [:dispatch [::results/init {:order (or 
                                                                                             (keyword (:query/order search-query))
                                                                                             results-db/default-order)}]])
              (when (or reload? (and (or not-same-order? not-same-query?) (:query/blueprints search-query)))
                [:dispatch [::complete-query-and-search search-query {:not-same-query? (or reload? not-same-query?)}]])]})))))

(rf/reg-event-fx
 ::complete-query-and-search
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [search-query opts]]
  {:db (assoc db :loading-blueprints? true)
   ::network/request [{:endpoint :dictionary/complete-blueprints}
                      {:body (stringify-keys-with-namespaces {:query/blueprints (:query/blueprints search-query)})
                       :on-success [::complete-query-and-search-success {:search-query search-query :opts opts}]}]}))

(rf/reg-event-fx
 ::complete-query-and-search-success
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [search-query opts ::fetch-fx/response]}]]
  (let [body (:body response)
        pills (-> (pills/coerce (:query/pills body))
                  (pills/pills->flats))
        search-query (assoc search-query :query/pills pills)
        senses (-> body :query/pills pills/coerce pills/pills-senses)
        processf #(when-not (str/blank? %)
                    (-> %
                        str/trim
                        str/lower-case
                        (str/replace "-" " ")))
        text-pills (remove nil?
                           (mapcat #(cond
                                      (= :text (:pill/type %))
                                      [(processf (:pill/query %))]
                                      (#{:senses :term} (:pill/type %))
                                      [(processf (:pill/canonical-label %))
                                       (processf (:pill/label %))]) pills))]
    {:db (-> db 
             (assoc :search-query search-query)
             (assoc :loading-blueprints? false)
             (assoc :senses senses)
             (assoc :free-text text-pills))
     :dispatch [::search opts]})))

(rf/reg-event-fx
 ::search
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [_ [{:keys [not-same-query?]}]]
  {:fx [(when (or (nil? not-same-query?) not-same-query?) [:dispatch [::widgets/fetch]])
        [:dispatch [::results/fetch {}]]]}))

(rf/reg-event-fx
 ::remove-from-query
 [rf/trim-v]
 (fn-traced
  [{db :db} [parent pill children]]
  (let [search-db (get db db-path)
        app-db (get db app-events/db-path)
        user (:user app-db)
        route (query-utils/route (remove-from-query
                                  (:search-query search-db) parent pill children) (roles-hash user))]
    {:dispatch [::router/redirect-to-path-with-query-params (->route :search) (:query-params route)]})))

(rf/reg-event-fx
 ::replace-in-query
 [rf/trim-v]
 (fn-traced
  [{db :db} [parent pill]]
  (let [search-db (get db db-path)
        app-db (get db app-events/db-path)
        user (:user app-db)
        route (query-utils/route (replace-in-query
                                  (:search-query search-db) parent pill) (roles-hash user))]
    {:dispatch [::router/redirect-to-path-with-query-params (->route :search) (:query-params route)]})))

; Filters

(rf/reg-event-fx
 ::reset-widget-filters
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} _]
  {:db (assoc db :widget-filters {} :last-selected-filter-key nil)
   :fx [[:dispatch [::search]]
        [:dispatch [::searchbar-events/clear-counts [db-path]]]]}))

(rf/reg-event-fx
 ::clear-widget-filters
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [filter-keys]]
  {:db (-> db
           (update :widget-filters merge
                   (zipmap filter-keys (repeat #{})))
           (assoc :last-selected-filter-key nil))
   :dispatch [::search]}))

(rf/reg-event-fx
 ::adjust-widget-filter
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [filter-key item op]]
  {:db (-> db
           (update-in [:widget-filters filter-key] (fnil op #{}) item)
           (assoc :last-selected-filter-key filter-key))
   :dispatch [::search]}))


(rf/reg-event-fx
 ::widget-filter-select
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [filter-key item]]
  (let [f (get-in db [:widget-filters filter-key])
        item? (contains? f item)
        op (if item? disj conj)]
    {:dispatch [::adjust-widget-filter filter-key item op]})))

(rf/reg-event-fx
 ::reset-widget-filter
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [filter-key item]]
  {:db (-> db
           (assoc-in [:widget-filters filter-key] #{item})
           (assoc :last-selected-filter-key filter-key))
   :dispatch [::search]}))

(rf/reg-event-fx
 ::promote-widget-filters
 [(rf/path db-path)]
 (fn [{:keys [db]} _]
   (let [{:keys [search-query widget-filters]} db
         new-query (promoted-query search-query widget-filters)]
     {:db (-> db
              (assoc :search-query new-query)
              (assoc :widget-filters {}))
      :dispatch [::submit-query]})))

(rf/reg-event-db
 ::clear-suggestions
 [(rf/path db-path)]
 (fn-traced
  [db _]
  (assoc-in db [:suggestions :current] nil)))

(rf/reg-event-fx
 ::submit-query
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{:keys [db]} _]
  (let [search-query (cond-> (:search-query db)
                       (not (get-in db [:search-query :query/type])) (assoc :query/type :articles))
        new-query-params (select-keys (:query-params (query-utils/route search-query nil)) [:query])
        old-query-params (select-keys (-> (get-in db [:route])
                                          (query-params)) [:query])]
    {:fx [(if (= new-query-params old-query-params)
            (if (= (router/current-route) :search)
             [:dispatch [::init
                         (assoc (:query-params (query-utils/route search-query nil)) :reload? true)]]
             [:dispatch [::router/redirect-to-path-with-query-params
                        (router/->route :search)
                        (assoc (:query-params (query-utils/route search-query nil)) :reload? true)]])
            [:dispatch [::router/redirect-to-path-with-query-params
                        (router/->route :search)
                        (:query-params (query-utils/route search-query nil))]])
          [:dispatch [::searchbar-events/clear-counts [db-path]]]]})))

;; -- Subscriptions

(rf/reg-sub
 ::state
 (fn [db _]
   (db-path db)))

(rf/reg-sub
 ::search-query
 :<- [::state]
 (fn [state _]
   (get state :search-query)))

(rf/reg-sub
 ::loading-blueprints?
 :<- [::state]
 (fn [state _]
   (get state :loading-blueprints?)))


(rf/reg-sub
 ::filtered-query
 :<- [::search-query]
 :<- [::widget-filters]
 (fn [[search-query widget-filters] _]
   (first (analysis-query search-query widget-filters []))))

(rf/reg-sub
 ::widget-filters
 :<- [::state]
 (fn [{:keys [:widget-filters]} _]
   widget-filters))

(rf/reg-sub
 ::filter-query
 :<- [::widget-filters]
 (fn [widget-filters]
   (filter-pills widget-filters)))

(rf/reg-sub
 ::last-selected-filter-key
 :<- [::state]
 (fn [{:keys [:last-selected-filter-key]} _]
   last-selected-filter-key))

(rf/reg-sub
 ::widget-filter
 :<- [::state]
 (fn [{:keys [:widget-filters]} [key]]
   (get widget-filters key)))

(defn has-filter?
  [filters key]
  (boolean (seq (filter identity (get filters key)))))

(rf/reg-sub
 ::widget-has-filter?
 :<- [::widget-filters]
 (fn [filters [_ key]]
   (has-filter? filters key)))

(defn has-filters?
  [filters keys]
  (some true? (map (partial has-filter? filters) keys)))

(rf/reg-sub
 ::widget-has-filters?
 :<- [::widget-filters]
 (fn [filters [_ keys]]
   (has-filters? filters keys)))

(rf/reg-sub
 ::suggestions
 :<- [::state]
 (fn [state _]
   (:suggestions state)))
